/**
 * Candid RPA status dialog with header tabs for auth letter, documents,
 * and notes. Body content is chosen by {@link CandidRpaBodyRenderer}.
 *
 * @module candidRpaStatusModal
 */
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ModalId } from "../../../enums/modalId";
import { CandidWritebackRecord } from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { CandidWritebacksByUseCase } from "../../../pages/worklists/singleOrder/candidUpload/utils/groupCandidWritebacksByUseCase";
import { RootState } from "../../../redux/store/store";
import CandidRpaBodyRenderer from "./candidRpaBodyRenderer";
import CandidRpaHeaderNav from "./candidRpaHeaderNav";

/** Props for {@link CandidRpaStatusModal}. */
interface CandidRpaStatusModalProps {
  /** Dismisses the dialog and clears Redux `openedModalId`. */
  onClose: () => void;
}

/**
 * Shape of `state.modal.metaData` when the Candid RPA status modal is open.
 */
interface CandidRpaStatusModalMetaData {
  /** Header tab value (`auth-letter` | `documents` | `notes`). */
  selectedNavItem?: string;
  /** Optional selected row/item id within the active tab. */
  selectedItem?: string;
  /** Write-back rows grouped by use case for the body panels. */
  groupedByUseCase?: CandidWritebacksByUseCase;
  /** Write-back row that opened the modal (from the table view icon). */
  rowData?: CandidWritebackRecord;
}

/**
 * Full-height Candid details dialog. Seeds the header tab from Redux
 * `metaData.selectedNavItem` and delegates the body to
 * {@link CandidRpaBodyRenderer}.
 *
 * @param props.onClose - Called when the dialog is dismissed
 * @returns The Candid RPA status modal
 */
const CandidRpaStatusModal = ({ onClose }: CandidRpaStatusModalProps) => {
  const metaData = useSelector((state: RootState) => state.modal.metaData) as
    CandidRpaStatusModalMetaData | undefined;

  const [selectedNavItem, setSelectedNavItem] = useState<string>("");
  useEffect(() => {
    setSelectedNavItem(metaData?.selectedNavItem || "");
  }, [metaData?.selectedNavItem]);
  return (
    <Modal
      dialogId={ModalId.CANDID_RPA_STATUS_MODAL}
      onClose={onClose}
      onSave={() => {}}
      title={"View Candid Details"}
      saveButtonText={""}
      cancelText={""}
      hideFooter
    >
      <div className="candid-rpa-status--container flex h-full flex-col gap-3 overflow-hidden pb-4">
        <div className="header-nav border-b border-primaryGray-14">
          <CandidRpaHeaderNav
            selectedNavItem={selectedNavItem}
            onTabChange={setSelectedNavItem}
          />
        </div>
        <CandidRpaBodyRenderer selectedNavItem={selectedNavItem} />
      </div>
    </Modal>
  );
};

export default CandidRpaStatusModal;
